/**
 * MEDalign listicle page behaviour. Three small custom elements, no framework.
 *
 *  <listicle-utm-links>   copies ad-tracking query params (utm_*, fbclid, gclid,
 *                         ttclid) from the landing URL onto every [data-ls-cta]
 *                         link inside it, so the product page keeps attribution.
 *  <listicle-sticky-cta>  mobile bar that appears after the reader scrolls past
 *                         [data-ls-sticky-after] and hides while any
 *                         [data-ls-sticky-hide] element (the offer, the final
 *                         CTA) is on screen.
 *  <listicle-scroll-link> smooth-scrolls to an in-page anchor, honouring
 *                         prefers-reduced-motion.
 *
 * Every element wires its listeners through an AbortController in
 * connectedCallback and aborts in disconnectedCallback, so Theme Editor
 * re-renders (shopify:section:load / unload) never leak listeners.
 */

const TRACKING_KEYS = ['fbclid', 'gclid', 'ttclid', 'msclkid', 'sccid'];

function trackingParams() {
  const out = new URLSearchParams();
  const src = new URLSearchParams(window.location.search);
  src.forEach((value, key) => {
    if (key.startsWith('utm_') || TRACKING_KEYS.includes(key)) out.set(key, value);
  });
  return out;
}

class ListicleUtmLinks extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.apply();
    document.addEventListener('shopify:section:load', () => this.apply(), { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  apply() {
    const params = trackingParams();
    if (![...params.keys()].length) return;
    this.querySelectorAll('a[data-ls-cta]').forEach((a) => {
      try {
        const url = new URL(a.getAttribute('href'), window.location.origin);
        if (url.origin !== window.location.origin) return;
        params.forEach((v, k) => {
          if (!url.searchParams.has(k)) url.searchParams.set(k, v);
        });
        a.setAttribute('href', url.pathname + url.search + url.hash);
      } catch (e) {
        /* leave non-URL hrefs (mailto:, #anchor) untouched */
      }
    });
  }
}

class ListicleStickyCta extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.bar = this.querySelector('[data-ls-sticky-bar]');
    if (!this.bar) return;

    this.pastTrigger = false;
    this.blocked = 0;
    this.pinned = false;

    const trigger = document.querySelector('[data-ls-sticky-after]');
    if (trigger && 'IntersectionObserver' in window) {
      this.triggerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.pastTrigger = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          });
          this.update();
        },
        { threshold: 0 }
      );
      this.triggerObserver.observe(trigger);
    } else {
      this.pastTrigger = true;
    }

    const blockers = document.querySelectorAll('[data-ls-sticky-hide]');
    if (blockers.length && 'IntersectionObserver' in window) {
      this.blockObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.__lsVisible = entry.isIntersecting;
          });
          this.blocked = [...blockers].filter((el) => el.__lsVisible).length;
          this.update();
        },
        { threshold: 0.15 }
      );
      blockers.forEach((el) => this.blockObserver.observe(el));
    }

    // Theme Editor: keep the bar visible while the merchant is editing it.
    document.addEventListener(
      'shopify:section:select',
      (e) => {
        if (e.target && e.target.contains(this)) {
          this.pinned = true;
          this.update();
        }
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (e) => {
        if (e.target && e.target.contains(this)) {
          this.pinned = false;
          this.update();
        }
      },
      { signal }
    );

    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.triggerObserver?.disconnect();
    this.blockObserver?.disconnect();
  }

  update() {
    const show = this.pinned || (this.pastTrigger && this.blocked === 0);
    this.bar.setAttribute('data-visible', show ? 'true' : 'false');
    this.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    this.bar.querySelectorAll('a, button').forEach((el) => {
      el.tabIndex = show ? 0 : -1;
    });
  }
}

class ListicleScrollLink extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.addEventListener(
      'click',
      (e) => {
        const a = e.target.closest('a[href^="#"]');
        if (!a) return;
        const target = document.querySelector(a.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      },
      { signal: this.controller.signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get('listicle-utm-links')) customElements.define('listicle-utm-links', ListicleUtmLinks);
if (!customElements.get('listicle-sticky-cta')) customElements.define('listicle-sticky-cta', ListicleStickyCta);
if (!customElements.get('listicle-scroll-link')) customElements.define('listicle-scroll-link', ListicleScrollLink);
